import React, { useState, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import classNames from 'classnames';
import { ProductContent } from './ProductContent';
import { ProductManager } from './ProductManager';
import * as utils from '../../../../utils';
import {
    useUpdateProjectTagItemMutation,
    useUpdateProjectTagItemQtyMutation,
    useUpdateProjectTagItemSkuMutation,
    useDeleteProjectTagItemMutation,
    useCopyProjectTagItemMutation,
    useSubmitProductDocJobMutation,
    useSubmitProductDocWorkflowJobMutation,
    mainActions,
    mainSelectors,
    documentsApi,
    projectsApi,
} from '../../../../store';
import { SkuProductContent } from './SkuProductContent';
import { CheckboxField } from '../../../../components';

export const ProductRow = (props) => {
    const { tag, tagItem, opsDisable, provided, onCheckboxToggle, isChecked } =
        props;

    const dispatch = useDispatch();

    const selectedItemId = useSelector(mainSelectors.getSelectedItemId);
    const [, setCpqData] = utils.cpqDataState.useStateContext();

    const [qtyValue, setQtyValue] = useState(tagItem.quantity);
    const [mouseEnetered, setMouseEntered] = useState(false);
    const [updateTagItem] = useUpdateProjectTagItemMutation();
    const [updateTagItemQty] = useUpdateProjectTagItemQtyMutation();
    const [updateTagSkuItem] = useUpdateProjectTagItemSkuMutation();
    const [deleteTagItem] = useDeleteProjectTagItemMutation();
    const [copyTagItem] = useCopyProjectTagItemMutation();
    const [submitBuildJob] = useSubmitProductDocJobMutation();
    const [submitWorkflowBuildJob] = useSubmitProductDocWorkflowJobMutation();
    const configObj = useMemo(() => {
        return tagItem.configuratorJson
            ? JSON.parse(tagItem.configuratorJson)
            : null;
    }, [tagItem.configuratorJson]);

    let productId = configObj?.idProduct;
    const prodConfig = configObj?.configuredProduct;

    const prodHasError = useMemo(() => {
        if (!configObj) {
            return false;
        }
        return configObj.configuredProduct?.hasErrors;
    }, [configObj]);

    const saveItem = async (value) => {
        const itemQty =
            value?.fields?.find((x) => x.name === 'fQuantity')?.value ?? 1;
        const itemFix = value?.fields?.find((x) => x.name === 'rebarSelection');
        if (itemFix) {
            itemFix.stringValue = itemFix.stringValue + '';
        }
        dispatch(
            mainActions.showWaitingModal({
                isSimple: true,
                msg: 'Saving product item ...',
            }),
        );
        try {
            await updateTagItem({
                id: tag.projectId,
                tagId: tag.id,
                qty: itemQty,
                config: JSON.stringify(value),
                itemId: tagItem.id,
            }).unwrap();
        } catch (err) {
            console.log(err);
        } finally {
            dispatch(mainActions.hideWaitingModal());
        }
    };

    const saveSkuItem = async (value) => {
        dispatch(
            mainActions.showWaitingModal({
                isSimple: true,
                msg: 'Saving sku product item ...',
            }),
        );
        try {
            await updateTagSkuItem({
                id: tag.projectId,
                tagId: tag.id,
                itemId: tagItem.id,
                productId: value,
            }).unwrap();
        } catch (err) {
            console.log(err);
        } finally {
            dispatch(mainActions.hideWaitingModal());
        }
    };

    const saveItemQty = async (value) => {
        if (value !== tagItem.quantity) {
            dispatch(
                mainActions.showWaitingModal({
                    isSimple: true,
                    msg: 'Saving product item quantity ...',
                }),
            );
            try {
                await updateTagItemQty({
                    id: tag.projectId,
                    tagId: tag.id,
                    qty: value,
                    itemId: tagItem.id,
                }).unwrap();
            } catch (err) {
                console.log(err);
            } finally {
                dispatch(mainActions.hideWaitingModal());
            }
        }
    };

    const toggleCustomQty = async () => {
        dispatch(
            mainActions.showWaitingModal({
                isSimple: true,
                msg: 'Updating custom quantity setting ...',
            }),
        );
        try {
            await updateTagItemQty({
                id: tag.projectId,
                tagId: tag.id,
                qty: tagItem.quantity,
                itemId: tagItem.id,
                useCustomQty: !tagItem.useCustomQty,
            }).unwrap();
        } catch (err) {
            console.log(err);
        } finally {
            dispatch(mainActions.hideWaitingModal());
        }
    };

    const deleteItem = async () => {
        dispatch(
            mainActions.showWaitingModal({
                isSimple: true,
                msg: 'Deleting product item ...',
            }),
        );
        try {
            onCheckboxToggle?.(tagItem.id, false);
            await deleteTagItem({
                id: tag.projectId,
                tagId: tag.id,
                itemId: tagItem.id,
            }).unwrap();
        } catch (err) {
            console.log(err);
        } finally {
            dispatch(mainActions.hideWaitingModal());
        }
    };

    const copyItem = async () => {
        dispatch(
            mainActions.showWaitingModal({
                isSimple: true,
                msg: 'Copying product item ...',
            }),
        );
        try {
            await copyTagItem({
                id: tag.projectId,
                tagId: tag.id,
                itemId: tagItem.id,
            }).unwrap();
        } catch (err) {
            console.log(err);
        } finally {
            dispatch(mainActions.hideWaitingModal());
        }
    };

    const genDocument = async (value) => {
        const { buildType, isSaleSubmittal } = value;
        if (!isSaleSubmittal) {
            dispatch(
                mainActions.showWaitingModal({
                    isSimple: true,
                    msg: 'Generating Calculation Summary ...',
                }),
            );
        } else {
            dispatch(
                mainActions.showWaitingModal({
                    isSimple: false,
                    msg: 'Generating Sale Submittal\nPlease wait as it will take a while .....',
                }),
            );
        }
        try {
            await submitBuildJob({
                id: tag.projectId,
                tagId: tag.id,
                itemId: tagItem.id,
                buildType: buildType,
            }).unwrap();
            setCpqData({ showModal: false, itemNeedRefresh: tagItem.id });
        } catch (err) {
            console.log(err);
        } finally {
            dispatch(mainActions.hideWaitingModal());
            dispatch(
                documentsApi.util.prefetch(
                    'getProductHasJobInProgress',
                    tagItem.id,
                    { force: true },
                ),
            );
            dispatch(
                projectsApi.util.prefetch('getProjectReady', tag.projectId, {
                    force: true,
                }),
            );
        }
    };

    const newGenDocument = async (value) => {
        const reqObj = {
            ...value,
            designTag: `["${tag.value}"]`,
        };
        dispatch(
            mainActions.showWaitingModal({
                isSimple: true,
                msg: 'Submitting item document job...',
            }),
        );
        try {
            await submitWorkflowBuildJob({
                id: tag.projectId,
                tagId: tag.id,
                itemId: tagItem.id,
                req: reqObj,
            }).unwrap();
            setCpqData({ itemNeedRefresh: tagItem.id });
        } catch (err) {
            console.log(err);
        } finally {
            dispatch(mainActions.hideWaitingModal());
            dispatch(
                documentsApi.util.prefetch(
                    'getProductHasJobInProgress',
                    tagItem.id,
                    { force: true },
                ),
            );
            dispatch(
                projectsApi.util.prefetch('getProjectReady', tag.projectId, {
                    force: true,
                }),
            );
        }
    };

    const isSelected = useMemo(() => {
        return selectedItemId === tagItem.id;
    }, [tagItem.id, selectedItemId]);

    const isSkuProduct = useMemo(() => {
        return tagItem.productId != null;
    }, [tagItem.productId]);

    const onClickHandler = (isSelected, selectedItemId) => {
        if (!isSelected) {
            dispatch(mainActions.setSelectedItemId(selectedItemId));
            dispatch(mainActions.setSelectedTagId(null));
            setQtyValue(tagItem.quantity);
        } else {
            dispatch(mainActions.setSelectedItemId(null));
            setMouseEntered(false);
        }
    };

    const numFieldClickHandler = (event) => {
        event.stopPropagation();
    };

    const numFieldValueChangeHandler = (event) => {
        if (event.target.value === '') {
            setQtyValue(1);
        }
        if (parseInt(event.target.value) < 1) {
            setQtyValue(1);
        }
        setQtyValue(+event.target.value);
    };

    const keyUpHandler = (event) => {
        if (event.key === 'Enter') {
            if (qtyValue >= 1) {
                saveItemQty(qtyValue);
            }
            setMouseEntered(false);
        }
    };

    const mouseLeaveHandler = () => {
        if (mouseEnetered) {
            if (qtyValue !== tagItem.quantity) {
                saveItemQty(qtyValue);
            }
            setMouseEntered(false);
        }
    };

    return (
        <tr
            className={classNames({
                'text-white bg-green-dark': isSelected,
                'bg-green-secondary': isChecked && !isSelected,
                'hover:bg-gray-5': !isSelected && !isChecked,
            })}
            ref={provided?.innerRef}
            {...provided?.draggableProps}
            {...provided?.dragHandleProps}
        >
            <td className="px-2">
                <CheckboxField
                    checked={isChecked}
                    onChange={() => {
                        const newCheckedState = !isChecked;
                        onCheckboxToggle?.(tagItem.id, newCheckedState);
                    }}
                    title={`Select product ${tagItem.name}`}
                />
            </td>
            <td
                colSpan="5"
                onClick={() => {
                    onClickHandler(isSelected, tagItem.id);
                }}
            >
                {isSkuProduct && (
                    <SkuProductContent
                        tagItem={tagItem}
                        onSave={saveSkuItem}
                    />
                )}
                {!isSkuProduct && (
                    <ProductContent
                        firstRow={tagItem.name}
                        secondRow={tagItem.info}
                        needValidate={tagItem.needValidate}
                        needAction={tagItem.needAction}
                        hasError={prodHasError}
                    />
                )}
            </td>
            <td
                colSpan="1"
                className="text-right"
            >
                <div className="flex justify-end items-center">
                    <div className="swegon-like-checkbox-wrapper">
                        <CheckboxField
                            checked={tagItem.useCustomQty || false}
                            onChange={toggleCustomQty}
                            title="Use custom quantity for this product"
                        />
                    </div>
                </div>
            </td>
            <td
                colSpan="1"
                onClick={() => {
                    setMouseEntered(!mouseEnetered);
                }}
                onMouseLeave={mouseLeaveHandler}
            >
                <div
                    className="swegon-ui-quantity-input-wrapper"
                    onMouseEnter={() => {
                        setMouseEntered(!mouseEnetered);
                    }}
                    onMouseLeave={mouseLeaveHandler}
                >
                    <input
                        type="number"
                        defaultValue={qtyValue}
                        min={1}
                        onClick={numFieldClickHandler}
                        onChange={numFieldValueChangeHandler}
                        onKeyUp={keyUpHandler}
                        disabled={!mouseEnetered && !opsDisable}
                    />
                </div>
            </td>
            <td className="w-1 text-left">
                <div className="text-black-30">({tagItem.totalProductQty})</div>
            </td>
            <td colSpan="1">
                <ProductManager
                    productId={productId}
                    prodConfig={prodConfig}
                    onSave={saveItem}
                    onDelete={deleteItem}
                    onCopy={copyItem}
                    onBuildDoc={genDocument}
                    onBuildWorkflowDoc={newGenDocument}
                    opsDisable={opsDisable}
                    opPhase="perform menu options"
                />
            </td>
        </tr>
    );
};
